import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { SALES_AI_ASSISTANT, SALES_QUICK_SUGGESTIONS, SALES_TIME_GREETINGS, SALES_INVITATIONS } from '@/lib/sales-chat/constants';
import { cn } from '@/lib/core/utils';

// Get time-based greeting
const getTimeGreeting = (): string => {
  const hour = new Date().getHours();
  let period: 'morning' | 'afternoon' | 'evening' | 'night';

  if (hour >= 5 && hour < 12) period = 'morning';
  else if (hour >= 12 && hour < 17) period = 'afternoon';
  else if (hour >= 17 && hour < 21) period = 'evening';
  else period = 'night';

  const greetings = SALES_TIME_GREETINGS[period];
  return greetings[Math.floor(Math.random() * greetings.length)];
};

// Get random invitation
const getInvitation = (): string => {
  return SALES_INVITATIONS[Math.floor(Math.random() * SALES_INVITATIONS.length)];
};

interface SalesWelcomeScreenProps {
  onSuggestionClick: (text: string) => void;
}

export const SalesWelcomeScreen: React.FC<SalesWelcomeScreenProps> = ({ onSuggestionClick }) => {
  const greeting = getTimeGreeting();
  const invitation = getInvitation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center justify-center h-full px-4"
    >
      {/* AI Avatar */}
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="w-14 h-14 rounded-full flex items-center justify-center ring-1 ring-border mb-4 bg-background"
      >
        <img
          src="/media/tag-innovation-logo.png"
          alt={SALES_AI_ASSISTANT.name}
          className="h-8 w-8 rounded-sm"
        />
      </motion.div>

      {/* Greeting */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="text-center mb-4"
      >
        <h2 className="text-base font-medium text-foreground">
          {greeting}!
        </h2>
        <p className="text-sm text-muted-foreground mt-1">{invitation}</p>
      </motion.div>

      {/* Quick suggestions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="w-full max-w-[300px] space-y-1"
      >
        {SALES_QUICK_SUGGESTIONS.map((suggestion, index) => (
          <motion.button
            key={suggestion.text}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + index * 0.08 }}
            onClick={() => onSuggestionClick(suggestion.text)}
            className={cn(
              'w-full group flex items-center gap-2 py-2.5 px-3 rounded-lg',
              'text-left text-sm text-muted-foreground',
              'hover:bg-muted/50 hover:text-foreground',
              'transition-colors duration-150'
            )}
          >
            <span className="flex-1">{suggestion.text}</span>
            <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-50 group-hover:translate-x-0 transition-all duration-150" />
          </motion.button>
        ))}
      </motion.div>

      {/* Tagline */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-xs text-muted-foreground/50 mt-4 text-center"
      >
        {SALES_AI_ASSISTANT.tagline}
      </motion.p>
    </motion.div>
  );
};

export default SalesWelcomeScreen;
